/* The timer on a piece of work (owner, 2026-10-09: time from start to end on each task,
 * automatic or by hand, as the organisation decides).
 *
 * Start when you begin, Stop when you leave it: the stop logs the real start and end on the
 * timesheet. One clock per person -- starting here stops the one running elsewhere, and says
 * so. Sending your reply stops it too (the gateway does), so in "automatic" nobody types hours.
 */
import { useEffect, useState } from 'react'
import { useToast } from '@jackdesk/shell'
import type { ErpError } from '../api'
import { useErp } from '../context'
import type { RunningTimer, TimeMode } from './types'

/** h:mm:ss since a moment. */
export function clock(startedAt: string, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/** Hours since a moment, to the hundredth: what the hours box is filled with. */
export function hoursSince(startedAt: string, now = Date.now()): number {
  return Math.max(0.01, Math.round(((now - new Date(startedAt).getTime()) / 3_600_000) * 100) / 100)
}

/** Re-render every second while a clock is on screen. */
export function useTick(on: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!on) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [on])
  return now
}

export function Timer({ name, running, mode, onChanged }: {
  name: string
  running: RunningTimer | null | undefined
  mode: TimeMode | undefined
  onChanged: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const [started, setStarted] = useState<string | null>(running?.task === name ? running.started_at : null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { setStarted(running?.task === name ? running.started_at : null) }, [running, name])
  const now = useTick(Boolean(started))
  if (mode === 'manual') return null

  async function go(action: 'start' | 'stop') {
    setBusy(true)
    try {
      const done = await erp.workTimer(name, action)
      if (done.stopped) {
        const elsewhere = done.stopped.task !== name
        toast('ok', `${elsewhere ? 'Your other timer stopped: ' : ''}${done.stopped.hours} h logged`
          + `${done.stopped.capped ? ' (stopped at 12 hours: it had been left running)' : ''}.`)
      }
      setStarted(done.running?.task === name ? done.running.started_at : null)
      onChanged()
    } catch (e) {
      toast('error', (e as ErpError).message)
    } finally {
      setBusy(false)
    }
  }

  return started ? (
    <button className="btn btn-sm" onClick={() => void go('stop')} disabled={busy}
            aria-label={`Stop the timer: ${clock(started, now)} so far`}>
      <span aria-hidden="true" style={{ color: 'var(--danger)' }}>●</span> {clock(started, now)} · Stop
    </button>
  ) : (
    <button className="btn btn-sm" onClick={() => void go('start')} disabled={busy}
            title={mode === 'automatic' ? 'Time here is recorded by the timer' : 'Time this work'}>
      ⏱ Start timer
    </button>
  )
}
